import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { useToast } from '../hooks/use-toast';
import AddressAutocomplete from '../components/AddressAutocomplete';
import { 
  Package, MapPin, Clock, DollarSign, Plus, X, Calendar,
  Truck, ChevronRight, User, Phone, Mail, ArrowLeft,
  CheckCircle, Loader2, Star, Navigation, Shield, Heart, Home, Briefcase,
  Share2, Copy, ExternalLink
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Helper function to safely extract error message from API responses
const getErrorMessage = (error, fallbackMsg = 'Something went wrong') => {
  const detail = error.response?.data?.detail;
  if (!detail) return fallbackMsg;
  if (Array.isArray(detail)) {
    // FastAPI/Pydantic validation errors return an array of objects
    return detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
  }
  if (typeof detail === 'string') return detail;
  if (typeof detail === 'object') return detail.msg || detail.message || JSON.stringify(detail);
  return fallbackMsg;
};

// Package size presets (dimensions in inches, weight in lbs)
const PACKAGE_PRESETS = [
  { id: 'envelope', name: 'Envelope', icon: '✉️', length: 12, width: 9, height: 1, weight: 1, description: 'Documents, letters' },
  { id: 'small', name: 'Small Box', icon: '📦', length: 12, width: 10, height: 8, weight: 10, description: 'Books, small items' },
  { id: 'medium', name: 'Medium Box', icon: '📦', length: 18, width: 14, height: 12, weight: 30, description: 'Electronics, appliances' },
  { id: 'large', name: 'Large Box', icon: '📦', length: 24, width: 18, height: 18, weight: 50, description: 'Multiple items, bulk' },
  { id: 'xlarge', name: 'Extra Large', icon: '🗃️', length: 36, width: 24, height: 24, weight: 100, description: 'Furniture pieces' },
  { id: 'furniture', name: 'Furniture', icon: '🛋️', length: 72, width: 36, height: 36, weight: 200, description: 'Sofas, tables, beds' },
];

// Vehicle capacities (dimensions in inches, weight in lbs)
const VEHICLE_CAPACITIES = {
  'Small Size Sprinter': { length: 96, width: 60, height: 54, weight: 1500, icon: '🚐' },
  'Large Sprinter': { length: 170, width: 70, height: 72, weight: 3500, icon: '🚚' },
  '20ft Box Truck': { length: 240, width: 96, height: 96, weight: 6000, icon: '🚛' },
  '26ft Box Truck': { length: 312, width: 102, height: 102, weight: 10000, icon: '🚛' },
  // Default for unknown vehicles
  'default': { length: 120, width: 60, height: 60, weight: 2000, icon: '🚐' }
};

const DeliveryApp = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  
  // Auth state
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [showAuth, setShowAuth] = useState(false);
  const [authMode, setAuthMode] = useState('login');
  const [authEmail, setAuthEmail] = useState('');
  const [authPassword, setAuthPassword] = useState('');
  const [authName, setAuthName] = useState('');
  const [authPhone, setAuthPhone] = useState('');
  const [isAuthLoading, setIsAuthLoading] = useState(false);
  
  // Delivery booking state
  const [pickup, setPickup] = useState('');
  const [pickupCoords, setPickupCoords] = useState(null);
  const [dropoff, setDropoff] = useState('');
  const [dropoffCoords, setDropoffCoords] = useState(null);
  const [stops, setStops] = useState([]);
  const [packageDescription, setPackageDescription] = useState('');
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [vehicleOptions, setVehicleOptions] = useState([]);
  const [isFetchingPrices, setIsFetchingPrices] = useState(false);
  const [estimatedDistance, setEstimatedDistance] = useState(null);
  const [estimatedTime, setEstimatedTime] = useState(null);
  
  // Package size estimator state
  const [showSizeEstimator, setShowSizeEstimator] = useState(false);
  const [packageDimensions, setPackageDimensions] = useState({
    length: '',
    width: '',
    height: '',
    weight: ''
  });
  const [selectedPreset, setSelectedPreset] = useState(null);
  
  // Schedule state
  const [isScheduled, setIsScheduled] = useState(false);
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');
  const [availableSlots, setAvailableSlots] = useState([]);
  
  // Booking state
  const [isBooking, setIsBooking] = useState(false);
  const [bookingStep, setBookingStep] = useState('form'); // 'form', 'review', 'payment'
  
  // Share menu state
  const [showShareMenu, setShowShareMenu] = useState(false);
  
  // Delivery history state (for "Order Again")
  const [showHistory, setShowHistory] = useState(false);
  const [deliveryHistory, setDeliveryHistory] = useState([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  
  // Favorite addresses state
  const [favoriteAddresses, setFavoriteAddresses] = useState([]);
  const [showSaveAddressModal, setShowSaveAddressModal] = useState(false);
  const [addressToSave, setAddressToSave] = useState(null);
  const [saveAddressLabel, setSaveAddressLabel] = useState('');

  // Check for existing session
  useEffect(() => {
    const userId = localStorage.getItem('delivery_user_id');
    const userName = localStorage.getItem('delivery_user_name');
    if (userId && userName) {
      setIsLoggedIn(true);
      setCurrentUser({ id: userId, name: userName, email: localStorage.getItem('delivery_user_email') });
    }
  }, []);

  // Share functionality
  const getShareUrl = () => {
    return window.location.origin + '/delivery-app';
  };
  
  const shareMessage = "Book fast & reliable deliveries with J-Style Delivery! 📦🚚";
  
  const shareToSocial = (platform) => {
    const url = encodeURIComponent(getShareUrl());
    const text = encodeURIComponent(shareMessage);
    
    const shareUrls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${url}&quote=${text}`,
      twitter: `https://twitter.com/intent/tweet?url=${url}&text=${text}`,
      whatsapp: `https://wa.me/?text=${text}%20${url}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${url}`,
      telegram: `https://t.me/share/url?url=${url}&text=${text}`,
    };
    
    if (shareUrls[platform]) {
      window.open(shareUrls[platform], '_blank', 'width=600,height=400');
    }
    setShowShareMenu(false);
  };
  
  const copyShareLink = async () => {
    try {
      await navigator.clipboard.writeText(getShareUrl());
      toast({ title: "Link Copied!", description: "Share it anywhere you like" });
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = getShareUrl();
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      toast({ title: "Link Copied!", description: "Share it anywhere you like" });
    }
    setShowShareMenu(false);
  };

  // Fetch favorite addresses when user is logged in
  const fetchFavoriteAddresses = useCallback(async () => {
    if (!currentUser?.id) return;
    try {
      const response = await axios.get(`${BACKEND_URL}/api/users/${currentUser.id}/addresses`);
      setFavoriteAddresses(response.data.addresses || []);
    } catch (error) {
      console.error('Error fetching favorite addresses:', error);
    }
  }, [currentUser]);

  useEffect(() => {
    if (isLoggedIn && currentUser?.id) {
      fetchFavoriteAddresses();
    }
  }, [isLoggedIn, currentUser, fetchFavoriteAddresses]);

  // Save a favorite address
  const saveFavoriteAddress = async (label) => {
    if (!addressToSave || !currentUser?.id) return;
    
    try {
      await axios.post(`${BACKEND_URL}/api/users/${currentUser.id}/addresses`, {
        label: label,
        address: addressToSave.address,
        lat: addressToSave.lat,
        lng: addressToSave.lng
      });
      
      toast({ title: "Address Saved!", description: `Saved as "${label}"` });
      setShowSaveAddressModal(false);
      setAddressToSave(null);
      setSaveAddressLabel('');
      fetchFavoriteAddresses();
    } catch (error) {
      toast({ 
        title: "Error", 
        description: error.response?.data?.detail || "Could not save address",
        variant: "destructive"
      });
    }
  };

  // Select a favorite address
  const selectFavoriteAddress = (addr, type) => {
    if (type === 'pickup') {
      setPickup(addr.address);
      if (addr.lat && addr.lng) {
        setPickupCoords({ lat: addr.lat, lng: addr.lng });
      }
    } else {
      setDropoff(addr.address);
      if (addr.lat && addr.lng) {
        setDropoffCoords({ lat: addr.lat, lng: addr.lng });
      }
    }
    toast({ title: `${addr.label} selected`, description: addr.address.split(',')[0] });
  };

  // Check if a vehicle can fit the package
  const canVehicleFitPackage = (vehicleName, dims) => {
    if (!dims.length || !dims.width || !dims.height) return true; // No dimensions = all vehicles OK
    
    const capacity = VEHICLE_CAPACITIES[vehicleName] || VEHICLE_CAPACITIES['default'];
    const pkgL = parseFloat(dims.length) || 0;
    const pkgW = parseFloat(dims.width) || 0;
    const pkgH = parseFloat(dims.height) || 0;
    const pkgWeight = parseFloat(dims.weight) || 0;
    
    // Check if package fits (considering rotation)
    const pkgDims = [pkgL, pkgW, pkgH].sort((a, b) => b - a); // Sort descending
    const vehDims = [capacity.length, capacity.width, capacity.height].sort((a, b) => b - a);
    
    const fits = pkgDims[0] <= vehDims[0] && pkgDims[1] <= vehDims[1] && pkgDims[2] <= vehDims[2];
    const weightOK = pkgWeight <= capacity.weight;
    
    return fits && weightOK;
  };

  // Get fit score for vehicle (lower = tighter fit = better match)
  const getVehicleFitScore = (vehicleName, dims) => {
    if (!dims.length || !dims.width || !dims.height) return 999;
    
    const capacity = VEHICLE_CAPACITIES[vehicleName] || VEHICLE_CAPACITIES['default'];
    const pkgVolume = (parseFloat(dims.length) || 0) * (parseFloat(dims.width) || 0) * (parseFloat(dims.height) || 0);
    const vehVolume = capacity.length * capacity.width * capacity.height;
    
    // Score is ratio of vehicle volume to package volume (lower = better fit)
    return vehVolume / Math.max(pkgVolume, 1);
  };

  // Apply a package preset - auto-selects best fitting vehicle
  const applyPackagePreset = (preset) => {
    setSelectedPreset(preset.id);
    const newDimensions = {
      length: preset.length.toString(),
      width: preset.width.toString(),
      height: preset.height.toString(),
      weight: preset.weight.toString()
    };
    setPackageDimensions(newDimensions);
    
    // Auto-select the best fitting vehicle
    if (vehicleOptions.length > 0) {
      const filtered = vehicleOptions
        .map(v => ({
          ...v,
          canFit: canVehicleFitPackage(v.name, newDimensions),
          fitScore: getVehicleFitScore(v.name, newDimensions)
        }))
        .filter(v => v.canFit)
        .sort((a, b) => a.fitScore - b.fitScore);
      
      if (filtered.length > 0) {
        const bestVehicle = filtered[0];
        setSelectedVehicle(bestVehicle.id);
        setPackageDescription(`${preset.name} - ${preset.description}`);
        
        toast({
          title: `${preset.name} selected`,
          description: `Best vehicle: ${bestVehicle.name} - $${bestVehicle.price?.toFixed(2) || 'N/A'}`,
        });
        
        // Scroll to vehicle section
        setTimeout(() => {
          const vehicleSection = document.querySelector('[data-testid="vehicle-options"]');
          if (vehicleSection) {
            vehicleSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 100);
      } else {
        toast({
          title: "Package too large",
          description: "No available vehicles can fit this package size",
          variant: "destructive"
        });
      }
    }
  };

  // Get filtered and sorted vehicle options
  const getFilteredVehicles = () => {
    if (!packageDimensions.length && !packageDimensions.weight) {
      return vehicleOptions; // No filtering if no dimensions
    }
    
    return vehicleOptions
      .map(v => ({
        ...v,
        canFit: canVehicleFitPackage(v.name, packageDimensions),
        fitScore: getVehicleFitScore(v.name, packageDimensions)
      }))
      .sort((a, b) => {
        // Vehicles that can fit come first, then sort by fit score
        if (a.canFit && !b.canFit) return -1;
        if (!a.canFit && b.canFit) return 1;
        return a.fitScore - b.fitScore;
      });
  };

  // Geocode an address using OpenStreetMap Nominatim API
  const geocodeAddress = async (address) => {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(address)}&countrycodes=us&limit=1`,
        { headers: { 'Accept': 'application/json', 'User-Agent': 'JStyleDelivery/1.0' } }
      );
      
      // Check if response is JSON
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        console.error('Geocoding returned non-JSON response');
        return null;
      }
      
      const data = await response.json();
      if (data && data.length > 0) {
        return { lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon) };
      }
      return null;
    } catch (error) {
      console.error('Geocoding error:', error);
      return null;
    }
  };

  // Fetch price estimates - uses default coords if geocoding fails
  const fetchPriceEstimates = useCallback(async () => {
    if (!pickup || !dropoff) return;
    
    setIsFetchingPrices(true);
    try {
      // Get or geocode pickup coordinates
      let pCoords = pickupCoords;
      if (!pCoords) {
        pCoords = await geocodeAddress(pickup);
        if (pCoords) {
          setPickupCoords(pCoords);
        } else {
          // Fallback: Use default NYC coordinates if geocoding fails
          pCoords = { lat: 40.7128, lng: -74.0060 };
          setPickupCoords(pCoords);
          console.log('Using default pickup coordinates');
        }
      }
      
      // Get or geocode dropoff coordinates
      let dCoords = dropoffCoords;
      if (!dCoords) {
        dCoords = await geocodeAddress(dropoff);
        if (dCoords) {
          setDropoffCoords(dCoords);
        } else {
          // Fallback: Use slightly different coords for dropoff
          dCoords = { lat: 40.7580, lng: -73.9855 };
          setDropoffCoords(dCoords);
          console.log('Using default dropoff coordinates');
        }
      }
      
      const response = await axios.post(`${BACKEND_URL}/api/rides/estimate`, {
        pickup_lat: pCoords.lat,
        pickup_lng: pCoords.lng,
        dropoff_lat: dCoords.lat,
        dropoff_lng: dCoords.lng,
        service_type: 'delivery',
        stops: stops.length
      });
      
      const estimates = response.data.estimates || [];
      setVehicleOptions(estimates.map(e => ({
        id: e.vehicle_id,
        name: e.vehicle_name,
        price: e.estimated_price,
        icon: e.icon || 'truck',
        description: e.description || 'Delivery vehicle'
      })));
      setEstimatedDistance(response.data.distance_miles);
      setEstimatedTime(response.data.estimated_time_minutes);
      
      if (estimates.length > 0 && !selectedVehicle) {
        setSelectedVehicle(estimates[0].vehicle_id);
      }
    } catch (error) {
      console.error('Error fetching prices:', error);
      toast({ title: "Error", description: "Could not fetch delivery prices. Please try again.", variant: "destructive" });
    } finally {
      setIsFetchingPrices(false);
    }
  }, [pickup, dropoff, pickupCoords, dropoffCoords, stops.length, selectedVehicle, toast]);

  // Fetch user's delivery history
  const fetchDeliveryHistory = useCallback(async () => {
    if (!currentUser?.id) return;
    
    setIsLoadingHistory(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/users/${currentUser.id}/deliveries?limit=10`);
      setDeliveryHistory(response.data.deliveries || []);
    } catch (error) {
      console.error('Error fetching delivery history:', error);
    } finally {
      setIsLoadingHistory(false);
    }
  }, [currentUser]);

  // Fetch delivery history when user is logged in
  useEffect(() => {
    if (isLoggedIn && currentUser?.id) {
      fetchDeliveryHistory();
    }
  }, [isLoggedIn, currentUser, fetchDeliveryHistory]);

  // Handle "Order Again" - pre-fill form with previous delivery details
  const handleOrderAgain = (delivery) => {
    setPickup(delivery.pickup?.address || '');
    setPickupCoords(delivery.pickup?.lat && delivery.pickup?.lng ? { lat: delivery.pickup.lat, lng: delivery.pickup.lng } : null);
    setDropoff(delivery.dropoff?.address || '');
    setDropoffCoords(delivery.dropoff?.lat && delivery.dropoff?.lng ? { lat: delivery.dropoff.lat, lng: delivery.dropoff.lng } : null);
    setPackageDescription(delivery.special_instructions || '');
    setShowHistory(false);
    toast({ title: "Form Pre-filled", description: "Delivery details loaded. Update if needed and book!" });
  };

  // Debounced price fetch - triggers when both addresses are entered
  useEffect(() => {
    const timer = setTimeout(() => {
      if (pickup && dropoff && pickup.length > 5 && dropoff.length > 5) {
        fetchPriceEstimates();
      }
    }, 1000);
    return () => clearTimeout(timer);
  }, [pickup, dropoff, fetchPriceEstimates]);

  // Fetch available time slots
  const fetchAvailableSlots = async (date) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/schedule/available-slots?date=${date}&service_type=delivery`);
      setAvailableSlots(response.data.slots || []);
    } catch (error) {
      console.error('Error fetching slots:', error);
    }
  };

  useEffect(() => {
    if (scheduledDate) {
      fetchAvailableSlots(scheduledDate);
    }
  }, [scheduledDate]);

  // Handle authentication
  const handleAuth = async (e) => {
    e.preventDefault();
    setIsAuthLoading(true);
    
    try {
      if (authMode === 'login') {
        // Login uses query parameters
        const response = await axios.post(
          `${BACKEND_URL}/api/users/login?email=${encodeURIComponent(authEmail)}&password=${encodeURIComponent(authPassword)}`
        );
        
        const userData = response.data;
        localStorage.setItem('delivery_user_id', userData.user_id);
        localStorage.setItem('delivery_user_name', userData.name);
        localStorage.setItem('delivery_user_email', userData.email);
        localStorage.setItem('delivery_user_phone', userData.phone || '');
        
        setCurrentUser({ id: userData.user_id, name: userData.name, email: userData.email });
        setIsLoggedIn(true);
        setShowAuth(false);
        toast({ title: "Welcome back!", description: `Logged in as ${userData.name}` });
      } else {
        // Signup uses JSON body
        const response = await axios.post(`${BACKEND_URL}/api/users/signup`, {
          full_name: authName,
          email: authEmail,
          phone: authPhone,
          password: authPassword
        });
        
        const userData = response.data;
        localStorage.setItem('delivery_user_id', userData.user_id);
        localStorage.setItem('delivery_user_name', authName);
        localStorage.setItem('delivery_user_email', userData.email);
        localStorage.setItem('delivery_user_phone', authPhone);
        
        setCurrentUser({ id: userData.user_id, name: authName, email: userData.email });
        setIsLoggedIn(true);
        setShowAuth(false);
        toast({ title: "Account created!", description: "Welcome to J-Style Delivery" });
      }
    } catch (error) {
      console.error('Auth error:', error.response?.data);
      toast({ 
        title: "Error", 
        description: getErrorMessage(error, "Authentication failed"), 
        variant: "destructive" 
      });
    } finally {
      setIsAuthLoading(false);
    }
  };

  // Handle logout
  const handleLogout = () => {
    localStorage.removeItem('delivery_user_id');
    localStorage.removeItem('delivery_user_name');
    localStorage.removeItem('delivery_user_email');
    setIsLoggedIn(false);
    setCurrentUser(null);
    toast({ title: "Logged out", description: "See you next time!" });
  };

  // Add stop
  const addStop = () => {
    if (stops.length < 5) {
      setStops([...stops, '']);
    }
  };

  // Remove stop
  const removeStop = (index) => {
    setStops(stops.filter((_, i) => i !== index));
  };

  // Update stop
  const updateStop = (index, value) => {
    const newStops = [...stops];
    newStops[index] = value;
    setStops(newStops);
  };

  // Get selected vehicle info
  const getSelectedVehicleInfo = () => {
    return vehicleOptions.find(v => v.id === selectedVehicle);
  };

  // Handle booking
  const handleBookDelivery = async () => {
    if (!isLoggedIn) {
      setShowAuth(true);
      return;
    }
    
    if (!pickup || !dropoff || !selectedVehicle) {
      toast({ title: "Missing info", description: "Please fill in pickup, dropoff, and select a vehicle", variant: "destructive" });
      return;
    }
    
    if (!pickupCoords || !dropoffCoords) {
      toast({ title: "Missing coordinates", description: "Please select addresses from the suggestions", variant: "destructive" });
      return;
    }
    
    setIsBooking(true);
    try {
      const vehicleInfo = getSelectedVehicleInfo();
      
      const bookingData = {
        user_id: currentUser.id,
        pickup: { address: pickup, lat: pickupCoords.lat, lng: pickupCoords.lng },
        dropoff: { address: dropoff, lat: dropoffCoords.lat, lng: dropoffCoords.lng },
        stops: stops.filter(s => s).map(s => ({ address: s })),
        vehicle_type: vehicleInfo?.name || 'Delivery',
        estimated_price: vehicleInfo?.price || 0,
        service_type: 'delivery',
        trip_type: 'one_way',
        special_instructions: packageDescription,
        is_scheduled: isScheduled,
        scheduled_time: isScheduled && scheduledTime ? scheduledTime : null,
        passenger_name: currentUser.name,
        passenger_phone: localStorage.getItem('delivery_user_phone') || ''
      };
      
      const response = await axios.post(`${BACKEND_URL}/api/rides/book`, bookingData);
      
      if (response.data.checkout_url) {
        // Store ride ID to redirect to tracking after payment
        if (response.data.ride_id) {
          localStorage.setItem('pending_delivery_track', response.data.ride_id);
        }
        window.location.href = response.data.checkout_url;
      } else if (response.data.ride_id) {
        toast({ title: "Delivery Booked!", description: "Redirecting to tracking..." });
        // Navigate to tracking page
        navigate(`/delivery-app/track/${response.data.ride_id}`);
      } else {
        toast({ title: "Delivery Booked!", description: "Your delivery request has been submitted" });
        // Reset form
        setPickup('');
        setPickupCoords(null);
        setDropoff('');
        setDropoffCoords(null);
        setStops([]);
        setPackageDescription('');
        setSelectedVehicle(null);
        setVehicleOptions([]);
        setBookingStep('form');
      }
    } catch (error) {
      console.error('Booking error:', error.response?.data);
      toast({ 
        title: "Booking failed", 
        description: getErrorMessage(error, "Could not book delivery"), 
        variant: "destructive" 
      });
    } finally {
      setIsBooking(false);
    }
  };

  // Get today's date for min date
  const today = new Date().toISOString().split('T')[0];

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50">
      {/* Header */}
      <header className="bg-gradient-to-r from-slate-900 to-slate-800 text-white p-4 sticky top-0 z-50 shadow-lg">
        <div className="container mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 bg-white rounded-lg p-0.5 shadow-md">
              <img 
                src="/jstyle-delivery-logo.jpg" 
                alt="J-Style Delivery" 
                className="w-full h-full rounded-md object-cover"
              />
            </div>
            <div>
              <h1 className="text-lg font-bold">J-Style Delivery</h1>
              <p className="text-xs text-gray-400">Fast & Reliable</p>
            </div>
          </div>
          
          {/* Share Button - Always visible */}
          <div className="relative">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => setShowShareMenu(!showShareMenu)}
              className="text-white hover:bg-white/10"
            >
              <Share2 size={18} />
              <span className="hidden sm:inline ml-1">Share</span>
            </Button>
            
            {/* Share Dropdown Menu */}
            {showShareMenu && (
              <div className="absolute right-0 top-full mt-2 w-56 bg-white rounded-xl shadow-2xl border border-gray-100 py-2 z-50">
                <div className="px-3 py-2 border-b border-gray-100">
                  <p className="text-xs text-gray-500 font-medium">Share J-Style Delivery</p>
                </div>
                
                <button
                  onClick={() => shareToSocial('facebook')}
                  className="w-full px-4 py-2.5 flex items-center gap-3 hover:bg-blue-50 transition-colors"
                >
                  <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
                    <span className="text-white text-sm font-bold">f</span>
                  </div>
                  <span className="text-gray-700">Facebook</span>
                </button>
                
                <button
                  onClick={() => shareToSocial('twitter')}
                  className="w-full px-4 py-2.5 flex items-center gap-3 hover:bg-gray-50 transition-colors"
                >
                  <div className="w-8 h-8 bg-black rounded-full flex items-center justify-center">
                    <span className="text-white text-sm font-bold">𝕏</span>
                  </div>
                  <span className="text-gray-700">X (Twitter)</span>
                </button>
                
                <button
                  onClick={() => shareToSocial('whatsapp')}
                  className="w-full px-4 py-2.5 flex items-center gap-3 hover:bg-green-50 transition-colors"
                >
                  <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                    <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                    </svg>
                  </div>
                  <span className="text-gray-700">WhatsApp</span>
                </button>
                
                <button
                  onClick={() => shareToSocial('linkedin')}
                  className="w-full px-4 py-2.5 flex items-center gap-3 hover:bg-blue-50 transition-colors"
                >
                  <div className="w-8 h-8 bg-blue-700 rounded-full flex items-center justify-center">
                    <span className="text-white text-sm font-bold">in</span>
                  </div>
                  <span className="text-gray-700">LinkedIn</span>
                </button>
                
                <button
                  onClick={() => shareToSocial('telegram')}
                  className="w-full px-4 py-2.5 flex items-center gap-3 hover:bg-blue-50 transition-colors"
                >
                  <div className="w-8 h-8 bg-sky-500 rounded-full flex items-center justify-center">
                    <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
                    </svg>
                  </div>
                  <span className="text-gray-700">Telegram</span>
                </button>
                
                <div className="border-t border-gray-100 mt-2 pt-2">
                  <button
                    onClick={copyShareLink}
                    className="w-full px-4 py-2.5 flex items-center gap-3 hover:bg-gray-50 transition-colors"
                  >
                    <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
                      <Copy size={16} className="text-gray-600" />
                    </div>
                    <span className="text-gray-700">Copy Link</span>
                  </button>
                </div>
              </div>
            )}
          </div>
          
          {isLoggedIn ? (
            <div className="flex items-center gap-2">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setShowHistory(!showHistory)} 
                className="text-white hover:bg-white/10"
              >
                <Clock size={16} className="mr-1" />
                <span className="hidden sm:inline">History</span>
              </Button>
              <span className="text-sm hidden sm:block">{currentUser?.name}</span>
              <Button variant="ghost" size="sm" onClick={handleLogout} className="text-white hover:bg-white/10">
                Logout
              </Button>
            </div>
          ) : (
            <Button onClick={() => setShowAuth(true)} className="bg-amber-500 hover:bg-amber-600">
              Sign In
            </Button>
          )}
        </div>
      </header>

      {/* Delivery History Panel */}
      {showHistory && isLoggedIn && (
        <div className="bg-white border-b shadow-sm">
          <div className="container mx-auto px-4 py-4 max-w-lg">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold text-gray-800 flex items-center gap-2">
                <Clock size={18} className="text-amber-500" />
                Past Deliveries
              </h3>
              <Button variant="ghost" size="sm" onClick={() => setShowHistory(false)}>
                <X size={18} />
              </Button>
            </div>
            
            {isLoadingHistory ? (
              <div className="text-center py-6">
                <Loader2 className="animate-spin mx-auto text-amber-500" size={24} />
                <p className="text-sm text-gray-500 mt-2">Loading history...</p>
              </div>
            ) : deliveryHistory.length === 0 ? (
              <div className="text-center py-6 text-gray-500">
                <Package size={32} className="mx-auto mb-2 opacity-50" />
                <p className="text-sm">No past deliveries yet</p>
              </div>
            ) : (
              <div className="space-y-3 max-h-64 overflow-y-auto">
                {deliveryHistory.map((delivery) => (
                  <div 
                    key={delivery.id} 
                    className="bg-gray-50 rounded-lg p-3 border border-gray-200"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs text-gray-500">
                            {new Date(delivery.created_at).toLocaleDateString()}
                          </span>
                          {delivery.user_rating && (
                            <span className="text-xs text-amber-500 flex items-center gap-0.5">
                              <Star size={10} fill="currentColor" />
                              {delivery.user_rating}
                            </span>
                          )}
                        </div>
                        <p className="text-sm font-medium text-gray-800 truncate">
                          {delivery.pickup?.address?.split(',')[0] || 'Pickup'}
                        </p>
                        <p className="text-xs text-gray-500 flex items-center gap-1">
                          <ChevronRight size={12} />
                          {delivery.dropoff?.address?.split(',')[0] || 'Dropoff'}
                        </p>
                        <p className="text-xs text-amber-600 font-medium mt-1">
                          ${delivery.estimated_price?.toFixed(2) || '0.00'}
                        </p>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => handleOrderAgain(delivery)}
                        className="bg-amber-500 hover:bg-amber-600 text-xs px-3"
                      >
                        Order Again
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="container mx-auto px-4 py-6 max-w-lg">
        {/* Hero Section */}
        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Send Anything, Anywhere</h2>
          <p className="text-gray-600 text-sm">Fast, reliable delivery at your fingertips</p>
        </div>

        {/* Booking Form */}
        <Card className="p-4 mb-4 border-2 border-amber-100 shadow-lg">
          <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
            <MapPin className="text-amber-500" size={20} />
            Delivery Details
          </h3>
          
          {/* Pickup */}
          <div className="mb-3">
            <label className="text-xs font-medium text-gray-500 mb-1 block">PICKUP LOCATION</label>
            <AddressAutocomplete
              value={pickup}
              onChange={setPickup}
              onSelect={(data) => {
                setPickup(data.address);
                if (data.lat && data.lng) {
                  setPickupCoords({ lat: data.lat, lng: data.lng });
                }
              }}
              placeholder="Enter pickup address"
              data-testid="delivery-pickup"
            />
            {/* Quick-select favorite addresses for pickup */}
            {favoriteAddresses.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {favoriteAddresses.slice(0, 3).filter(addr => addr.label).map((addr, idx) => (
                  <button
                    key={addr.id || `fav-pickup-${idx}`}
                    onClick={() => selectFavoriteAddress(addr, 'pickup')}
                    className="text-xs px-2 py-1 bg-amber-50 text-amber-700 rounded-full border border-amber-200 hover:bg-amber-100 transition-colors"
                  >
                    {addr.label === 'home' ? '🏠' : addr.label === 'work' ? '🏢' : '📍'} {addr.label}
                  </button>
                ))}
              </div>
            )}
            {/* Save pickup address button */}
            {pickup && pickupCoords && (
              <button
                onClick={() => {
                  setAddressToSave({ address: pickup, lat: pickupCoords.lat, lng: pickupCoords.lng });
                  setShowSaveAddressModal(true);
                }}
                className="text-xs text-amber-600 hover:text-amber-700 mt-1 flex items-center gap-1"
              >
                <Heart size={12} /> Save this address
              </button>
            )}
          </div>
          
          {/* Additional Stops */}
          {stops.map((stop, index) => (
            <div key={index} className="mb-3 relative">
              <label className="text-xs font-medium text-gray-500 mb-1 block">STOP {index + 1}</label>
              <div className="flex gap-2">
                <div className="flex-1">
                  <AddressAutocomplete
                    value={stop}
                    onChange={(value) => updateStop(index, value)}
                    placeholder={`Stop ${index + 1} address`}
                    data-testid={`delivery-stop-${index}`}
                  />
                </div>
                <Button variant="ghost" size="icon" onClick={() => removeStop(index)} className="text-red-500 hover:bg-red-50">
                  <X size={18} />
                </Button>
              </div>
            </div>
          ))}
          
          {stops.length < 5 && (
            <Button variant="outline" size="sm" onClick={addStop} className="mb-3 w-full border-dashed">
              <Plus size={16} className="mr-1" /> Add Stop
            </Button>
          )}
          
          {/* Dropoff */}
          <div className="mb-4">
            <label className="text-xs font-medium text-gray-500 mb-1 block">DROPOFF LOCATION</label>
            <AddressAutocomplete
              value={dropoff}
              onChange={setDropoff}
              onSelect={(data) => {
                setDropoff(data.address);
                if (data.lat && data.lng) {
                  setDropoffCoords({ lat: data.lat, lng: data.lng });
                }
              }}
              placeholder="Enter delivery address"
              data-testid="delivery-dropoff"
            />
            {/* Quick-select favorite addresses for dropoff */}
            {favoriteAddresses.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {favoriteAddresses.slice(0, 3).filter(addr => addr.label).map((addr, idx) => (
                  <button
                    key={addr.id || `fav-dropoff-${idx}`}
                    onClick={() => selectFavoriteAddress(addr, 'dropoff')}
                    className="text-xs px-2 py-1 bg-amber-50 text-amber-700 rounded-full border border-amber-200 hover:bg-amber-100 transition-colors"
                  >
                    {addr.label === 'home' ? '🏠' : addr.label === 'work' ? '🏢' : '📍'} {addr.label}
                  </button>
                ))}
              </div>
            )}
            {/* Save dropoff address button */}
            {dropoff && dropoffCoords && (
              <button
                onClick={() => {
                  setAddressToSave({ address: dropoff, lat: dropoffCoords.lat, lng: dropoffCoords.lng });
                  setShowSaveAddressModal(true);
                }}
                className="text-xs text-amber-600 hover:text-amber-700 mt-1 flex items-center gap-1"
              >
                <Heart size={12} /> Save this address
              </button>
            )}
          </div>
          
          {/* Package Description */}
          <div className="mb-4">
            <label className="text-xs font-medium text-gray-500 mb-1 block">PACKAGE DETAILS (Optional)</label>
            <Input
              value={packageDescription}
              onChange={(e) => setPackageDescription(e.target.value)}
              placeholder="Describe your package (size, contents, special handling)"
              className="border-2 border-gray-200 focus:border-amber-400 rounded-xl"
              data-testid="delivery-package-description"
            />
          </div>
        </Card>

        {/* Schedule Option */}
        <Card className="p-4 mb-4 border-2 border-amber-100 shadow-lg">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Calendar className="text-amber-500" size={20} />
              <span className="font-bold">Schedule Delivery</span>
            </div>
            <Button
              variant={isScheduled ? "default" : "outline"}
              size="sm"
              onClick={() => setIsScheduled(!isScheduled)}
              className={isScheduled ? "bg-amber-500 hover:bg-amber-600" : ""}
            >
              {isScheduled ? "Scheduled" : "Schedule Later"}
            </Button>
          </div>
          
          {isScheduled && (
            <div className="space-y-3">
              <Input
                type="date"
                value={scheduledDate}
                onChange={(e) => setScheduledDate(e.target.value)}
                min={today}
                className="border-2 border-gray-200 focus:border-amber-400 rounded-xl"
              />
              
              {availableSlots.length > 0 && (
                <div className="grid grid-cols-3 gap-2 max-h-40 overflow-y-auto">
                  {availableSlots.map((slot, index) => (
                    <Button
                      key={index}
                      variant={scheduledTime === slot.datetime_utc ? "default" : "outline"}
                      size="sm"
                      disabled={!slot.available}
                      onClick={() => setScheduledTime(slot.datetime_utc)}
                      className={`text-xs ${scheduledTime === slot.datetime_utc ? "bg-amber-500" : ""} ${!slot.available ? "opacity-50 bg-red-50" : ""}`}
                    >
                      {slot.time}
                    </Button>
                  ))}
                </div>
              )}
            </div>
          )}
        </Card>

        {/* Vehicle Selection */}
        {vehicleOptions.length > 0 && (
          <Card className="p-4 mb-4 border-2 border-amber-100 shadow-lg">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold text-lg flex items-center gap-2">
                <Truck className="text-amber-500" size={20} />
                Select Vehicle
              </h3>
              <button
                onClick={() => setShowSizeEstimator(!showSizeEstimator)}
                className={`text-xs px-3 py-1.5 rounded-full border transition-all flex items-center gap-1 ${
                  showSizeEstimator 
                    ? 'bg-amber-500 text-white border-amber-500' 
                    : 'bg-white text-amber-600 border-amber-300 hover:bg-amber-50'
                }`}
              >
                📐 Size Helper
              </button>
            </div>
            
            {/* Package Size Estimator */}
            {showSizeEstimator && (
              <div className="mb-4 p-4 bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl border border-amber-200">
                <p className="text-sm font-medium text-gray-700 mb-3">What are you sending?</p>
                
                {/* Quick Presets */}
                <div className="grid grid-cols-3 gap-2 mb-4">
                  {PACKAGE_PRESETS.map((preset) => (
                    <button
                      key={preset.id}
                      onClick={() => applyPackagePreset(preset)}
                      className={`p-2 rounded-lg border-2 transition-all text-center ${
                        selectedPreset === preset.id
                          ? 'border-amber-500 bg-white shadow-md'
                          : 'border-gray-200 bg-white hover:border-amber-300'
                      }`}
                    >
                      <span className="text-xl">{preset.icon}</span>
                      <p className="text-xs font-medium mt-1">{preset.name}</p>
                    </button>
                  ))}
                </div>
                
                {/* Custom Dimensions */}
                <div className="grid grid-cols-4 gap-2 mb-2">
                  <div>
                    <label className="text-xs text-gray-500 block mb-1">Length</label>
                    <div className="relative">
                      <input
                        type="number"
                        value={packageDimensions.length}
                        onChange={(e) => {
                          setPackageDimensions(prev => ({ ...prev, length: e.target.value }));
                          setSelectedPreset(null);
                        }}
                        placeholder="0"
                        className="w-full px-2 py-1.5 pr-6 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                      />
                      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-400">in</span>
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 block mb-1">Width</label>
                    <div className="relative">
                      <input
                        type="number"
                        value={packageDimensions.width}
                        onChange={(e) => {
                          setPackageDimensions(prev => ({ ...prev, width: e.target.value }));
                          setSelectedPreset(null);
                        }}
                        placeholder="0"
                        className="w-full px-2 py-1.5 pr-6 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                      />
                      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-400">in</span>
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 block mb-1">Height</label>
                    <div className="relative">
                      <input
                        type="number"
                        value={packageDimensions.height}
                        onChange={(e) => {
                          setPackageDimensions(prev => ({ ...prev, height: e.target.value }));
                          setSelectedPreset(null);
                        }}
                        placeholder="0"
                        className="w-full px-2 py-1.5 pr-6 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                      />
                      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-400">in</span>
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 block mb-1">Weight</label>
                    <div className="relative">
                      <input
                        type="number"
                        value={packageDimensions.weight}
                        onChange={(e) => {
                          setPackageDimensions(prev => ({ ...prev, weight: e.target.value }));
                          setSelectedPreset(null);
                        }}
                        placeholder="0"
                        className="w-full px-2 py-1.5 pr-7 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                      />
                      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-400">lbs</span>
                    </div>
                  </div>
                </div>
                
                {(packageDimensions.length || packageDimensions.weight) && (
                  <p className="text-xs text-amber-600 mt-2">
                    ✨ Vehicles are now sorted by best fit for your package
                  </p>
                )}
              </div>
            )}
            
            {estimatedDistance && (
              <div className="flex items-center gap-4 mb-3 text-sm text-gray-600 bg-gray-50 p-2 rounded-lg">
                <span><MapPin size={14} className="inline mr-1" />{estimatedDistance.toFixed(1)} mi</span>
                <span><Clock size={14} className="inline mr-1" />~{estimatedTime} min</span>
              </div>
            )}
            
            <div className="space-y-2" data-testid="vehicle-options">
              {getFilteredVehicles().map((vehicle, index) => {
                const isBestFit = index === 0 && packageDimensions.length && vehicle.canFit;
                const cantFit = packageDimensions.length && !vehicle.canFit;
                
                return (
                  <button
                    key={vehicle.id}
                    onClick={() => !cantFit && setSelectedVehicle(vehicle.id)}
                    disabled={cantFit}
                    className={`w-full p-3 rounded-xl border-2 transition-all flex items-center justify-between ${
                      cantFit
                        ? 'border-gray-200 bg-gray-50 opacity-50 cursor-not-allowed'
                        : selectedVehicle === vehicle.id 
                          ? 'border-amber-500 bg-amber-50' 
                          : isBestFit
                            ? 'border-green-400 bg-green-50 hover:border-green-500'
                            : 'border-gray-200 hover:border-amber-300'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        cantFit ? 'bg-gray-200' : isBestFit ? 'bg-green-100' : 'bg-amber-100'
                      }`}>
                        <span className="text-xl">
                          {VEHICLE_CAPACITIES[vehicle.name]?.icon || '🚐'}
                        </span>
                      </div>
                      <div className="text-left">
                        <div className="flex items-center gap-2">
                          <p className={`font-medium text-sm ${cantFit ? 'text-gray-400' : ''}`}>{vehicle.name}</p>
                          {isBestFit && (
                            <span className="text-xs bg-green-500 text-white px-2 py-0.5 rounded-full">Best Fit</span>
                          )}
                          {cantFit && (
                            <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded-full">Too Small</span>
                          )}
                        </div>
                        <p className={`text-xs ${cantFit ? 'text-gray-400' : 'text-gray-500'}`}>{vehicle.description}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className={`font-bold text-lg ${cantFit ? 'text-gray-400' : 'text-amber-600'}`}>
                        ${vehicle.price?.toFixed(2)}
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>
          </Card>
        )}

        {/* Loading Prices */}
        {isFetchingPrices && (
          <Card className="p-6 mb-4 border-2 border-amber-100 shadow-lg">
            <div className="flex items-center justify-center gap-2 text-gray-500">
              <Loader2 className="animate-spin" size={20} />
              <span>Getting prices...</span>
            </div>
          </Card>
        )}

        {/* Book Button */}
        <Button
          onClick={handleBookDelivery}
          disabled={!pickup || !dropoff || !selectedVehicle || isBooking}
          className="w-full py-6 text-lg font-bold bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 rounded-xl shadow-lg"
          data-testid="book-delivery-btn"
        >
          {isBooking ? (
            <><Loader2 className="animate-spin mr-2" /> Processing...</>
          ) : (
            <>
              <Package className="mr-2" size={20} />
              Book Delivery - ${getSelectedVehicleInfo()?.price?.toFixed(2) || '0.00'}
            </>
          )}
        </Button>

        {/* Trust Badges */}
        <div className="flex justify-center gap-4 mt-6 text-xs text-gray-500">
          <div className="flex items-center gap-1">
            <Shield size={14} className="text-green-500" />
            Secure Payment
          </div>
          <div className="flex items-center gap-1">
            <Star size={14} className="text-amber-500" />
            4.9 Rating
          </div>
          <div className="flex items-center gap-1">
            <Clock size={14} className="text-blue-500" />
            24/7 Service
          </div>
        </div>
      </main>

      {/* Auth Modal */}
      {showAuth && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
          <Card className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden">
            <div className="bg-gradient-to-r from-slate-900 to-slate-800 p-6 text-white">
              <button onClick={() => setShowAuth(false)} className="absolute top-4 right-4 p-2 hover:bg-white/10 rounded-full">
                <X size={20} />
              </button>
              <h2 className="text-xl font-bold">{authMode === 'login' ? 'Welcome Back' : 'Create Account'}</h2>
              <p className="text-gray-400 text-sm">Sign in to book your delivery</p>
            </div>
            
            <form onSubmit={handleAuth} className="p-6 space-y-4">
              {authMode === 'signup' && (
                <>
                  <div>
                    <label className="text-xs font-medium text-gray-500 mb-1 block">FULL NAME</label>
                    <Input
                      value={authName}
                      onChange={(e) => setAuthName(e.target.value)}
                      placeholder="John Doe"
                      required
                      className="border-2"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500 mb-1 block">PHONE NUMBER</label>
                    <Input
                      value={authPhone}
                      onChange={(e) => setAuthPhone(e.target.value)}
                      placeholder="(555) 123-4567"
                      required
                      className="border-2"
                    />
                  </div>
                </>
              )}
              
              <div>
                <label className="text-xs font-medium text-gray-500 mb-1 block">EMAIL</label>
                <Input
                  type="email"
                  value={authEmail}
                  onChange={(e) => setAuthEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  className="border-2"
                />
              </div>
              
              <div>
                <label className="text-xs font-medium text-gray-500 mb-1 block">PASSWORD</label>
                <Input
                  type="password"
                  value={authPassword}
                  onChange={(e) => setAuthPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  className="border-2"
                />
              </div>
              
              <Button
                type="submit"
                disabled={isAuthLoading}
                className="w-full py-3 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
              >
                {isAuthLoading ? <Loader2 className="animate-spin" /> : (authMode === 'login' ? 'Sign In' : 'Create Account')}
              </Button>
              
              <p className="text-center text-sm text-gray-500">
                {authMode === 'login' ? "Don't have an account? " : "Already have an account? "}
                <button
                  type="button"
                  onClick={() => setAuthMode(authMode === 'login' ? 'signup' : 'login')}
                  className="text-amber-600 font-medium hover:underline"
                >
                  {authMode === 'login' ? 'Sign Up' : 'Sign In'}
                </button>
              </p>
            </form>
          </Card>
        </div>
      )}
      
      {/* Save Address Modal */}
      {showSaveAddressModal && addressToSave && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-sm p-6 rounded-2xl shadow-2xl bg-white">
            <h3 className="text-lg font-bold text-gray-800 mb-2">Save Address</h3>
            <p className="text-sm text-gray-500 mb-4 truncate">{addressToSave.address}</p>
            
            {/* Quick labels */}
            <div className="flex gap-2 mb-4">
              <button
                onClick={() => saveFavoriteAddress('Home')}
                className="flex-1 flex items-center justify-center gap-2 py-3 bg-amber-50 text-amber-700 rounded-xl border-2 border-amber-200 hover:bg-amber-100 transition-colors"
              >
                <Home size={18} />
                Home
              </button>
              <button
                onClick={() => saveFavoriteAddress('Work')}
                className="flex-1 flex items-center justify-center gap-2 py-3 bg-blue-50 text-blue-700 rounded-xl border-2 border-blue-200 hover:bg-blue-100 transition-colors"
              >
                <Briefcase size={18} />
                Work
              </button>
            </div>
            
            {/* Custom label */}
            <div className="mb-4">
              <label className="text-xs text-gray-500 mb-1 block">Or use a custom name:</label>
              <div className="flex gap-2">
                <Input
                  value={saveAddressLabel}
                  onChange={(e) => setSaveAddressLabel(e.target.value)}
                  placeholder="e.g., Mom's House, Gym"
                  className="flex-1"
                />
                <Button
                  onClick={() => saveFavoriteAddress(saveAddressLabel)}
                  disabled={!saveAddressLabel.trim()}
                  className="bg-amber-500 hover:bg-amber-600"
                >
                  Save
                </Button>
              </div>
            </div>
            
            <Button
              variant="outline"
              className="w-full"
              onClick={() => {
                setShowSaveAddressModal(false);
                setAddressToSave(null);
                setSaveAddressLabel('');
              }}
            >
              Cancel
            </Button>
          </Card>
        </div>
      )}
    </div>
  );
};

export default DeliveryApp;
